if (!customElements.get('gc-tier-pricing')) {
  customElements.define(
    'gc-tier-pricing',
    class GCTierPricing extends HTMLElement {
      constructor() {
        super();
        this.onQuantityChange = this.update.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info') || document;
        this.quantityInput = this.productInfo.querySelector(`#Quantity-${this.dataset.sectionId}`);
        this.variantInput = this.productInfo.querySelector('.product-variant-id');
        this.rows = Array.from(this.querySelectorAll('[data-gc-tier-row]'));
        this.variantPrices = this.readVariantPrices();

        this.rows.forEach((row) => {
          row.addEventListener('click', () => this.selectTier(row));
        });

        if (this.quantityInput) {
          this.quantityInput.addEventListener('input', this.onQuantityChange);
          this.quantityInput.addEventListener('change', this.onQuantityChange);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const eventSectionId = event?.data?.sectionId;
            if (eventSectionId && eventSectionId !== this.dataset.sectionId) return;
            window.requestAnimationFrame(() => this.update());
          });
        }

        this.update();
      }

      disconnectedCallback() {
        if (this.quantityInput) {
          this.quantityInput.removeEventListener('input', this.onQuantityChange);
          this.quantityInput.removeEventListener('change', this.onQuantityChange);
        }
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      readVariantPrices() {
        try {
          return JSON.parse(this.querySelector('[data-gc-variant-prices]').textContent);
        } catch (error) {
          return {};
        }
      }

      getBasePrice() {
        this.variantInput = this.productInfo.querySelector('.product-variant-id');
        const variantId = this.variantInput?.value;
        const selectedPrice = Number(this.variantPrices[variantId]);
        if (Number.isFinite(selectedPrice)) return selectedPrice;
        const firstPrice = Number(Object.values(this.variantPrices)[0]);
        return Number.isFinite(firstPrice) ? firstPrice : 0;
      }

      selectTier(row) {
        if (!this.quantityInput) return;
        const minimum = Number(row.dataset.min) || 1;
        this.quantityInput.value = minimum;
        this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.quantityInput.focus({ preventScroll: true });
      }

      activeTier(quantity) {
        return (
          [...this.rows].reverse().find((row) => quantity >= (Number(row.dataset.min) || 1)) || this.rows[0]
        );
      }

      update() {
        const quantity = Math.max(1, Number(this.quantityInput?.value) || 1);
        const basePrice = this.getBasePrice();
        const activeRow = this.activeTier(quantity);

        this.rows.forEach((row) => {
          const discount = Number(row.dataset.discount) || 0;
          const unitPrice = Math.round((basePrice * (100 - discount)) / 100);
          const saving = Math.max(0, basePrice - unitPrice);
          const priceElement = row.querySelector('[data-gc-tier-price]');
          const savingElement = row.querySelector('[data-gc-tier-saving]');

          if (priceElement) priceElement.textContent = this.formatMoney(unitPrice);
          if (savingElement) {
            savingElement.hidden = saving <= 0;
            if (saving > 0) {
              const prefix = savingElement.dataset.prefix || 'Save';
              const suffix = savingElement.dataset.suffix || 'each';
              savingElement.textContent = `${prefix} ${this.formatMoney(saving)} ${suffix}`;
            }
          }

          const isActive = row === activeRow;
          row.classList.toggle('is-active', isActive);
          row.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        const activeDiscount = Number(activeRow?.dataset.discount) || 0;
        const activeUnitPrice = Math.round((basePrice * (100 - activeDiscount)) / 100);
        const total = activeUnitPrice * quantity;
        const totalSavings = Math.max(0, (basePrice - activeUnitPrice) * quantity);

        const quantityElement = this.querySelector('[data-gc-summary-quantity]');
        const unitElement = this.querySelector('[data-gc-summary-unit]');
        const totalElement = this.querySelector('[data-gc-summary-total]');
        if (quantityElement) quantityElement.textContent = quantity;
        if (unitElement) unitElement.textContent = this.formatMoney(activeUnitPrice);
        if (totalElement) totalElement.textContent = this.formatMoney(total);

        this.updateMainPrice(basePrice, activeUnitPrice, totalSavings);
      }

      updateMainPrice(basePrice, discountedPrice, totalSavings) {
        const priceContainer = this.productInfo.querySelector(`#price-${this.dataset.sectionId}`);
        if (!priceContainer) return;

        const nativePrice = priceContainer.querySelector('.price');
        const comparison = priceContainer.querySelector('[data-gc-price-comparison]');
        if (!comparison) return;

        const hasDiscount = discountedPrice < basePrice && totalSavings > 0;
        if (nativePrice) nativePrice.hidden = hasDiscount;
        comparison.hidden = !hasDiscount;

        if (!hasDiscount) return;

        const originalElement = comparison.querySelector('[data-gc-price-original]');
        const discountedElement = comparison.querySelector('[data-gc-price-discounted]');
        const savingsElement = comparison.querySelector('[data-gc-price-savings]');

        if (originalElement) originalElement.textContent = this.formatMoney(basePrice);
        if (discountedElement) discountedElement.textContent = this.formatMoney(discountedPrice);
        if (savingsElement) savingsElement.textContent = `You save ${this.formatMoney(totalSavings)}`;
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = Number(cents) || 0;
        const token = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
        let amount;

        if (token === 'amount_no_decimals') {
          amount = Math.round(value / 100).toLocaleString();
        } else if (token === 'amount_with_comma_separator') {
          amount = (value / 100).toFixed(2).replace('.', ',');
        } else if (token === 'amount_no_decimals_with_comma_separator') {
          amount = Math.round(value / 100).toLocaleString('de-DE');
        } else {
          amount = (value / 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }

        return format.replace(/\{\{\s*\w+\s*\}\}/, amount).replace(/&nbsp;/g, ' ').trim();
      }
    }
  );
}
